import { NgIf } from '@angular/common'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { DataMap } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    FeatureType,
    Grid,
    IncidentInvestigation,
    ListItem,
    ModalSize,
    User,
    VpJob,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { IncidentInvestigationModalComponent } from './incident-investigation-modal.component'

@Component({
    selector: 'app-incident-investigation-list',
    templateUrl: './incident-investigation-list.component.html',
    standalone: true,
    imports: [
        FormsModule,
        NgIf,
        FaIconComponent,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class IncidentInvestigationListComponent extends BaseComponent {
    faXmark = faXmark
    grid: Grid<IncidentInvestigation>

    reportingPartyMap!: DataMap
    supervisorMap!: DataMap
    jobMap!: DataMap

    activeUserList: ListItem<string>[] = []
    activeJobList: ListItem<string>[] = []
    allJobs: VpJob[] = []

    private userNameById = new Map<string, string>()

    get currentUserId(): string {
        return this.services.authentication.userId()
    }

    constructor() {
        super()
        this.grid = new Grid<IncidentInvestigation>(this.services)
            .setupModalEditing(
                this.services.data.incidentInvestigations,
                'Incident Investigation',
                IncidentInvestigationModalComponent,
                () => IncidentInvestigation.createNew(),
                this,
                ModalSize.extraLarge,
            )
            .wrapColumnHeaders()
            .filterBy(item => [
                item.location,
                item.reportingPartyName,
                item.supervisorName,
                item.involvedEmployeeNames,
                item.jobDisplay,
                item.incidentType,
                item.status,
                item.equipmentOrVehicleNumber,
            ])

        this.grid.modified.on(this.refreshItems.bind(this))
        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.users.queryAllByAction('GetForLookup'),
                    this.services.data.vpJobs.queryAll(),
                    this.services.data.incidentInvestigations.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()

                        const allUsers = result[0] as User[]
                        const activeJobs = result[1] as VpJob[]
                        const items = result[2] as IncidentInvestigation[]

                        this.userNameById = new Map(allUsers.map(u => [u.id, u.displayName]))
                        this.reportingPartyMap = new DataMap(allUsers, 'id', 'displayName')
                        this.supervisorMap = new DataMap(allUsers, 'id', 'displayName')
                        this.activeUserList = allUsers.filter(u => u.active).map(u => new ListItem(u.id, u.displayName))

                        this.allJobs = activeJobs
                        this.activeJobList = activeJobs
                            .slice()
                            .sort((a, b) => a.id.trim().localeCompare(b.id.trim(), undefined, { numeric: true }))
                            .map(job => new ListItem(job.id, `${job.id.trim()} ${job.name}`))
                        this.jobMap = new DataMap(
                            activeJobs.map(j => ({ id: j.id, display: `${j.id.trim()} ${j.name}` })),
                            'id',
                            'display',
                        )

                        this.assignDisplayFields(items)
                        this.grid.items = items
                    },
                    error: () => {
                        this.initialLoadingComplete()
                    },
                }),
        )
    }

    private refreshItems() {
        this.services.data.incidentInvestigations.queryAll().subscribe({
            next: items => {
                this.assignDisplayFields(items)
                this.grid.items = items
            },
        })
    }

    private assignDisplayFields(items: IncidentInvestigation[]) {
        for (const item of items) {
            item.reportingPartyName =
                item.reportingPartyName ||
                (item.reportingPartyUserId ? this.userNameById.get(item.reportingPartyUserId) ?? '' : '')
            item.supervisorName =
                item.supervisorName || (item.supervisorUserId ? this.userNameById.get(item.supervisorUserId) ?? '' : '')

            if (!item.involvedEmployeeNames && item.employees?.length) {
                item.involvedEmployeeNames = item.employees
                    .map(e => this.userNameById.get(e.userId ?? '') ?? '')
                    .filter(name => !!name)
                    .join(', ')
            }

            item.jobDisplay = this.jobDisplayForItem(item.jobNumber)
        }
    }

    jobDisplayForItem(jobId: string | null | undefined): string {
        if (!jobId) return ''
        const job = this.allJobs.find(j => j.id === jobId)
        return job ? `${job.id.trim()} ${job.name}` : jobId
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.SafetyIncidentInvestigationEdit])
    }

    deleteSelectedItem() {
        const selected = this.grid.selectedItem
        if (!selected) return
        this.services.modals.confirm('Delete', 'Are you sure you want to delete this Incident Investigation?').then(() => {
            this.services.data.incidentInvestigations.delete(selected).subscribe({
                next: () => this.refreshItems(),
            })
        })
    }
}
